import { useEffect, useState } from "react";
import AdminPageHeader from "../../components/admin/AdminPageHeader";
import RefreshButton from "../../components/admin/RefreshButton";
import { useAdminToast } from "../../context/AdminToastContext";
import { gamificationService } from "../../features/gamification/service";
import type { Quest } from "../../features/gamification/types";
import { OverviewPanel, GrantsPanel, TakebacksPanel, AuditPanel } from "./GamificationPanels";
import { ApprovalsPanel, BadgesPanel, ReportPanel, ReviewQueuePanel } from "./GamificationAdminPanels";
import ReferralPanel from "./ReferralPanel";
import SettingsPanel from "./GamificationSettingsPanel";
import QuestsPanel from "./GamificationQuestsPanel";
import UserLookupPanel from "./GamificationUserLookupPanel";

// ── Page ─────────────────────────────────────────────────────────────────────
type TabKey = "home" | "settings" | "quests" | "grants" | "takebacks" | "review" | "approvals" | "report" | "badges" | "audit" | "users" | "referral";
const TABS: { key: TabKey; label: string }[] = [
  { key: "home", label: "Home" },
  { key: "settings", label: "Settings" },
  { key: "quests", label: "Quests" },
  { key: "grants", label: "Who got what" },
  { key: "takebacks", label: "Take-backs" },
  { key: "review", label: "Review queue" },
  { key: "approvals", label: "Approvals" },
  { key: "report", label: "Report" },
  { key: "badges", label: "Badges" },
  { key: "referral", label: "Referral" },
  { key: "audit", label: "Audit log" },
  { key: "users", label: "Look up a user" },
];

export default function AdminGamificationPage() {
  const toast = useAdminToast();
  const [reloadKey, setReloadKey] = useState(0);
  const [tab, setTab] = useState<TabKey>("home");
  const [quests, setQuests] = useState<Quest[]>([]);

  // The quest list feeds the grants filter; the quest table has its own copy so edits stay local.
  useEffect(() => {
    gamificationService.listQuests().then(setQuests).catch(() => setQuests([]));
  }, [reloadKey]);

  return (
    <div className="space-y-6">
      <AdminPageHeader
        title="Gamification"
        subtitle="Quest rewards: budget, settings, what each quest pays, who got what, take-backs, approvals, the free-credit report, badges and the audit log"
        actions={<RefreshButton onRefresh={() => setReloadKey((k) => k + 1)} />}
        className="mb-0"
      />
      <div role="tablist" aria-label="Gamification sections" className="flex flex-wrap gap-1 border-b border-gray-800">
        {TABS.map((t) => (
          <button
            key={t.key}
            role="tab"
            aria-selected={tab === t.key}
            onClick={() => setTab(t.key)}
            className={`px-3 py-2 text-sm rounded-t-lg ${tab === t.key ? "text-white bg-gray-800" : "text-gray-400 hover:text-white"}`}
          >
            {t.label}
          </button>
        ))}
      </div>
      {tab === "home" && <OverviewPanel reloadKey={reloadKey} />}
      {tab === "settings" && <SettingsPanel key={reloadKey} toast={toast} />}
      {tab === "quests" && <QuestsPanel reloadKey={reloadKey} />}
      {tab === "grants" && <GrantsPanel quests={quests} />}
      {tab === "takebacks" && <TakebacksPanel key={reloadKey} />}
      {tab === "review" && <ReviewQueuePanel key={reloadKey} />}
      {tab === "approvals" && <ApprovalsPanel key={reloadKey} />}
      {tab === "report" && <ReportPanel key={reloadKey} />}
      {tab === "badges" && <BadgesPanel key={reloadKey} onOpenSettings={() => setTab("settings")} />}
      {tab === "referral" && <ReferralPanel key={reloadKey} />}
      {tab === "audit" && <AuditPanel key={reloadKey} />}
      {tab === "users" && <UserLookupPanel />}
    </div>
  );
}
